/**
 * Produktmodellbezug und DTE-Listung eines Artikels.
 *
 * Zwei Dinge, die dieselbe Ansicht braucht und die trotzdem streng getrennt
 * bleiben muessen:
 *
 *   * der Weg SKU -> Produktmodell (sichtbare V1, Punkt 7)
 *   * die Beziehung dieser SKU zum operativen ERP
 *
 * Die Trennung ist nicht kosmetisch. Der DTE-Status sagt nichts ueber Fokus,
 * Bestand, Sortimentsklasse, Preislistenfreigabe oder Lebenszyklus
 * (PRODUCT_DATA_MODEL.md 22.1) -- und diese Ansicht behauptet es auch nicht.
 *
 * Beide Vorgaenge haben EIGENE Berechtigungen: `model.write` fuer die
 * Zuordnung, `dte.listing` fuer die Listung. Die Listung wirkt in ein
 * Fremdsystem und ist deshalb von "Artikel bearbeiten" getrennt (22.4).
 */
import { useEffect, useState } from "react";
import type { ArticleDetail, DteListingStatus, MetaResponse } from "@homebase/shared";
import { DTE_TRANSITIONS } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { modelLabel } from "../i18n/terminology.ts";
import { valueLabel } from "./ModelVariants.tsx";
import "./ProductModels.css";

export function ArticleModelSection({ article, meta, mayWriteModels, mayListDte, onChanged, onError, onOpenModel }: {
  article: ArticleDetail;
  meta: MetaResponse;
  mayWriteModels: boolean;
  mayListDte: boolean;
  onChanged: (a: ArticleDetail) => void;
  onError: (e: unknown) => void;
  onOpenModel: (id: number) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [assigning, setAssigning] = useState(false);

  return (
    <>
      <h3 className="detail__section">{t("models.articleSection")}</h3>

      {/* Ein Platzhalterdatensatz sagt es selbst -- an der Stelle, an der
          jemand ihn sonst fuer eine bestellbare SKU halten wuerde. */}
      {article.numberIsPlaceholder && (
        <p className="models__note models__note--strong">
          <span className="badge badge--placeholder">{t("models.placeholderBadge")}</span>{" "}
          {article.placeholderNote ?? t("models.placeholderNote")}
        </p>
      )}

      <div className="detail__field">
        <div className="detail__label">{t("models.model")}</div>
        <div className="detail__value">
          {article.productModel
            ? (
              <button className="models__link" onClick={() => onOpenModel(article.productModel!.id)}>
                {/*
                  * EINMAL, WENN BEIDE DASSELBE SAGEN (§9). Die Regel
                  * steht in `modelLabel` und nicht hier -- sie gilt
                  * ueberall, wo ein Modell benannt wird.
                  */}
                {(() => {
                  const m = modelLabel(article.productModel!.code, article.productModel!.name);
                  return m.name === null
                    ? <code>{m.code}</code>
                    : <><code>{m.code}</code> · {m.name}</>;
                })()}
              </button>
            )
            /* Kein Produktmodell ist der Regelfall und bleibt es -- die Ebene
               ist verfuegbar, nicht erzwungen (2.3). */
            : <span className="models__none">{t("models.noModel")}</span>}
          {mayWriteModels && (
            <button className="btn models__confirm" onClick={() => setAssigning(!assigning)}>
              {article.productModel ? t("models.reassign") : t("models.assign")}
            </button>
          )}
        </div>
      </div>

      {assigning && mayWriteModels && (
        <AssignModelForm article={article}
          onDone={(a) => { setAssigning(false); if (a) onChanged(a); }} onError={onError} />
      )}

      {/* Die Variante, als deren SKU dieser Artikel gefuehrt wird. Fehlt sie,
          ist dieser Artikel keine Variante -- Zubehoer und Komponenten sind
          es nie. Das ist keine Luecke. */}
      {article.variant && (
        <div className="detail__field">
          <div className="detail__label">{t("models.variant")}</div>
          <div className="detail__value">
            {article.variant.focusRank !== null && (
              <span className="badge badge--focus" title={t("models.focusTitle")}>
                {t("models.focusRank", { rank: article.variant.focusRank })}
              </span>
            )}
            {article.variant.values.length === 0
              ? <span className="models__none">{t("models.noVariantValues")}</span>
              : (
                <ul className="models__variantvalues">
                  {article.variant.values.map((v) => (
                    <li key={v.dimensionId}>
                      <span className="models__fieldlabel">
                        {i18n.tCode("prop", v.definition, v.definition)}
                      </span>{" "}
                      {valueLabel(i18n, v, null)}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      )}

      <DteListing article={article} meta={meta} mayListDte={mayListDte}
        onChanged={onChanged} onError={onError} />
    </>
  );
}

/**
 * Zuordnung zu einem Produktmodell.
 *
 * Zur Auswahl stehen ausschliesslich Modelle, deren Produktfamilie dieser
 * Artikel angehoert -- die Hierarchie Produktfamilie -> Produktmodell ->
 * Artikel gilt auch hier (2.3). Der Server prueft dasselbe.
 */
function AssignModelForm({ article, onDone, onError }: {
  article: ArticleDetail;
  onDone: (a: ArticleDetail | null) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [models, setModels] = useState<{ id: number; code: string; name: string }[] | null>(null);
  const [selected, setSelected] = useState<number | "">(article.productModel?.id ?? "");

  // Die Modelle der Familien dieses Artikels -- geladen, wenn das Formular
  // erscheint, nicht auf Vorrat.
  useEffect(() => {
    let abgebrochen = false;
    Promise.all(article.families.map((f) => api.models({ familyId: f.id })))
      .then((rs) => {
        if (abgebrochen) return;
        setModels(rs.flatMap((r) => r.items).map((m) => ({ id: m.id, code: m.code, name: m.name })));
      })
      .catch((e) => { if (!abgebrochen) { onError(e); setModels([]); } });
    return () => { abgebrochen = true; };
    // `onError` ist stabil (setState); die Abhaengigkeit ist die Artikelkennung.
  }, [article.id]);

  async function save(): Promise<void> {
    try { onDone(await api.assignArticleModel(article.id, selected === "" ? null : Number(selected))); }
    catch (e) { onError(e); onDone(null); }
  }

  return (
    <div className="models__form">
      <select className="list__select" value={selected} aria-label={t("models.model")}
              onChange={(e) => setSelected(e.target.value === "" ? "" : Number(e.target.value))}>
        <option value="">{t("models.noModel")}</option>
        {(models ?? []).map((m) => (
          <option key={m.id} value={m.id}>{m.name} ({m.code})</option>
        ))}
      </select>
      <button className="btn btn--primary" onClick={save}>{t("common.save")}</button>
      <button className="btn" onClick={() => onDone(null)}>{t("common.cancel")}</button>
      {models !== null && models.length === 0 && (
        <p className="models__note">{t("models.noModelsInFamily")}</p>
      )}
    </div>
  );
}

/**
 * DTE-Listungsstatus einer SKU.
 *
 * Nur die fachlich vorgesehenen Uebergaenge erscheinen zur Auswahl -- die
 * Tabelle steht in `shared`, damit Oberflaeche und Server denselben Stand
 * benutzen. `fehlgeschlagen` ist kein Endzustand: von dort fuehrt der Weg
 * zurueck nach `vorgemerkt`, ohne dass die Vorgeschichte verlorengeht (22.3).
 */
function DteListing({ article, meta, mayListDte, onChanged, onError }: {
  article: ArticleDetail;
  meta: MetaResponse;
  mayListDte: boolean;
  onChanged: (a: ArticleDetail) => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<DteListingStatus | "">("");
  const [note, setNote] = useState("");

  const current = article.dte.status;
  const targets = DTE_TRANSITIONS[current];
  // Die Nachschlagetabelle kommt aus der Datenbank; hier wird nur gefiltert.
  const available = meta.dteListingStatuses
    .filter((s) => (targets as readonly string[]).includes(s.code));

  async function save(): Promise<void> {
    if (status === "") return;
    try {
      onChanged(await api.setDteListing(article.id, {
        status,
        note: note.trim() === "" ? null : note.trim(),
        // Das Entlisten einer gelisteten SKU verlangt eine Bestaetigung (22.4).
        confirmDelisting: current === "gelistet" && status === "nicht_gelistet"
          ? window.confirm(t("dte.delistConfirm"))
          : undefined,
      }));
      setOpen(false); setStatus(""); setNote("");
    } catch (e) { onError(e); }
  }

  return (
    <>
      <div className="detail__field">
        <div className="detail__label">{t("dte.label")}</div>
        <div className="detail__value">
          <span className={`models__dte models__dte--${current}`}>{i18n.tCode("dte", current)}</span>
          {article.dte.listedAt && (
            <span className="models__since">
              {t("dte.since", { date: new Date(article.dte.listedAt).toLocaleDateString(i18n.lang) })}
            </span>
          )}
          {article.dte.note && <div className="models__dtenote">{article.dte.note}</div>}
          {mayListDte && available.length > 0 && (
            <button className="btn models__confirm" onClick={() => setOpen(!open)}>{t("dte.change")}</button>
          )}
        </div>
      </div>

      {/* Nicht gelistet bedeutet KEIN Bestandsdatum -- ausdruecklich nicht
          Bestand 0 (PRODUCT_DATA_MODEL.md 22.8, Architektur 7.6). */}
      {current === "nicht_gelistet" && <p className="models__note">{t("dte.notListedNote")}</p>}

      {open && mayListDte && (
        <div className="models__form">
          <select className="list__select" value={status} aria-label={t("dte.label")}
                  onChange={(e) => setStatus(e.target.value as DteListingStatus | "")}>
            <option value="">{t("models.choose")}</option>
            {available.map((s) => (
              <option key={s.code} value={s.code}>{i18n.tCode("dte", s.code)}</option>
            ))}
          </select>
          <input className="list__search" value={note} onChange={(e) => setNote(e.target.value)}
                 placeholder={t("dte.notePlaceholder")} aria-label={t("dte.note")} />
          <button className="btn btn--primary" onClick={save} disabled={status === ""}>{t("common.save")}</button>
          <button className="btn" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
        </div>
      )}

      {/* Zeitpunkt, ausloesende Person und Ergebnis je Vorgang -- keine
          Vollversionierung (22.4). */}
      {article.dte.events.length > 0 && (
        <ul className="models__events">
          {article.dte.events.map((e) => (
            <li key={e.id}>
              <span className="models__eventtime">
                {new Date(e.occurredAt).toLocaleString(i18n.lang)}
              </span>
              {" "}
              {e.from ? i18n.tCode("dte", e.from) : "—"} → {i18n.tCode("dte", e.to)}
              {e.actor && <span className="models__eventactor">{e.actor}</span>}
              {e.note && <span className="models__eventnote">{e.note}</span>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
